import { inject, Injectable, Signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { EventObject, NodeSingular } from 'cytoscape';

import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { SelectedNode } from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import {
    GenericSynopticItem,
    GraceThdSynopticItem,
    Layer,
    PlanTypeEnum,
    Synoptic,
} from '@connect-services/client/sdk/api';
import { SynopticMapData } from '@connect-services/client/services/broadcast-channel.service';
import { HasPermissionsService } from '@connect-services/client/services/has-permissions.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { NetworkPathService } from '@connect-services/client/services/network-path/network-path.service';
import { OpticalPathExportService } from '@connect-services/client/services/optical-path-export.service';
import { ExportSynopticService } from '@connect-services/client/services/synoptic/export-synoptic.service';
import { DepositWithStandard } from '@connect-services/client/services/synoptic/export-synoptic-cartouche.service';
import {
    EntityInformation,
    MapSynopticRedirectionService,
} from '@connect-services/client/services/synoptic/map-synoptic-redirection.service';
import { SelectSynopticService } from '@connect-services/client/services/synoptic/select-synoptic.service';
import { SynopticGraphService } from '@connect-services/client/services/synoptic/synoptic-graph.service';
import { SynopticPlanLayoutService } from '@connect-services/client/services/synoptic/synoptic-plan-layout.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

interface MenuItem {
    content: string;
    hasTrailingDivider: boolean;
    id: string;
    onClickFunction?: (evt: EventObject) => void;
    selector: string;
    submenu?: MenuItem[];
}

@Injectable({
    providedIn: 'root',
})
export class SynopticContextMenuService {
    private readonly broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.SYNOPTIC);
    private readonly exportSynopticService = inject(ExportSynopticService);
    private readonly hasPermissionsService = inject(HasPermissionsService);
    private readonly homeService = inject(HomeService);
    private readonly layerListBloc = inject(LayerListBloc);
    private readonly mapSynopticRedirectionService = inject(MapSynopticRedirectionService);
    private readonly networkPathService = inject(NetworkPathService);
    private readonly opticalPathExportService = inject(OpticalPathExportService);
    private readonly selectSynopticService = inject(SelectSynopticService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly synopticGraphService = inject(SynopticGraphService);
    private readonly synopticPlanLayoutService = inject(SynopticPlanLayoutService);
    private readonly translocoService = inject(TranslocoService);

    public generateMenu(isGraceThd: boolean, synoptic: Signal<Synoptic | null>): Record<string, unknown> {
        const menuItems: MenuItem[] = isGraceThd
            ? this.generateGraceThdMenu(synoptic)
            : this.generateGenericMenu(synoptic);

        return {
            contextMenuClasses: ['custom-context-menu'],
            menuItemClasses: ['custom-context-menu-item'],
            menuItems,
            submenuIndicator: { height: 12, src: '/assets/images/icons/chevron-right.svg', width: 12 },
        };
    }

    private copyTextToClipboard(text?: string): void {
        if (!text) {
            this.snackBarService.openErrorSnackBar('synoptic.contextMenu.dataCopyFailed');

            return;
        }

        navigator.clipboard.writeText(text);

        this.snackBarService.openSuccessSnackBar('synoptic.contextMenu.dataCopySuccess');
    }

    private findLineSynopticItem(node: SelectedNode): (item: GraceThdSynopticItem | GenericSynopticItem) => boolean {
        return (item: GraceThdSynopticItem | GenericSynopticItem): boolean => {
            return node.dataNode.dataId === item.edge;
        };
    }

    private async findNetworkPathLayer(
        node: SelectedNode,
        synopticItem: GenericSynopticItem | GraceThdSynopticItem,
        depositId: string,
    ): Promise<Layer | undefined> {
        const entityInformation: EntityInformation = await this.mapSynopticRedirectionService.findEntityInLayerService(
            synopticItem,
            node.dataNode.uuid,
            depositId,
            LayerStyleTypeEnum.SYMBOL,
        );

        const layerStyleId: string | undefined = entityInformation.layerStyle?.id;

        if (!layerStyleId) {
            return;
        }

        return this.layerListBloc.value()?.items?.find((layer: Layer) => layer.layerStyle === layerStyleId);
    }

    private findPointSynopticItem(node: SelectedNode): (item: GraceThdSynopticItem | GenericSynopticItem) => boolean {
        return (item: GraceThdSynopticItem | GenericSynopticItem): boolean => {
            //Format id example : "BPPFEYL0000266" ( =/= uuid )
            return node.dataNode.id === item.from || node.dataNode.id === item.to;
        };
    }

    private findSynopticItem(
        node: SelectedNode,
        synoptic: Synoptic,
    ): GenericSynopticItem | GraceThdSynopticItem | undefined {
        return synoptic.data.find(
            (item: GraceThdSynopticItem | GenericSynopticItem) =>
                node.dataNode.id === item.from || node.dataNode.id === item.to,
        );
    }

    private generateCopyNodeMenu(type: PlanTypeEnum): MenuItem {
        const selector = `node.is-${type}`;
        const suffix = type === PlanTypeEnum.BOX ? '' : `-${PlanTypeEnum.BAY}`;

        const submenu: MenuItem[] = [
            {
                content: this.translocoService.translate('synoptic.contextMenu.copyGraceId'),
                hasTrailingDivider: false,
                id: `copy-grace-id${suffix}`,
                onClickFunction: (evt: EventObject): void => {
                    const node = this.getClickedNode(evt);

                    this.copyTextToClipboard(node.dataNode.graceIdentifier);
                },
                selector,
            },
            {
                content: this.translocoService.translate('synoptic.contextMenu.copyExternalCode', {
                    TYPE: type,
                }),
                hasTrailingDivider: false,
                id: `copy-external-code-${type}`,
                onClickFunction: (evt: EventObject): void => {
                    const node = this.getClickedNode(evt);

                    this.copyTextToClipboard(node.dataNode.externalCode);
                },
                selector,
            },
        ];

        if (type === PlanTypeEnum.BOX) {
            submenu.push({
                content: this.translocoService.translate('synoptic.contextMenu.copyPtechExternalCode'),
                hasTrailingDivider: false,
                id: 'copy-external-code-technical-point',
                onClickFunction: (evt: EventObject): void => {
                    const node = this.getClickedNode(evt);

                    this.copyTextToClipboard(node.dataNode.codeExt);
                },
                selector,
            });
        }

        submenu.push({
            content: this.translocoService.translate('synoptic.contextMenu.copyAddress'),
            hasTrailingDivider: false,
            id: `copy-address${suffix}`,
            onClickFunction: (evt: EventObject): void => {
                const node = this.getClickedNode(evt);

                this.copyTextToClipboard(node.dataNode.address);
            },
            selector,
        });

        return {
            content: this.translocoService.translate('copy'),
            hasTrailingDivider: true,
            id: `copy-${type}-menu`,
            selector,
            submenu,
        };
    }

    private generateGenericMenu(synoptic: Signal<Synoptic | null>): MenuItem[] {
        const centerOnMapTranslation = this.translocoService.translate('synoptic.contextMenu.centerOnMap');
        const copyIdTranslation = this.translocoService.translate('synoptic.contextMenu.copyId');

        return [
            {
                content: this.translocoService.translate('synoptic.contextMenu.exportFromItem'),
                hasTrailingDivider: false,
                id: 'export-svg',
                onClickFunction: (evt: EventObject): void => {
                    this.prepareNodeBeforeExportAsSvg(evt);
                },
                selector: 'node',
            },
            {
                content: centerOnMapTranslation,
                hasTrailingDivider: false,
                id: 'center-node-on-map',
                onClickFunction: async (evt: EventObject): Promise<void> => {
                    const node = this.getClickedNode(evt);

                    await this.redirectSynopticToMap(node, LayerStyleTypeEnum.SYMBOL, synoptic);
                },
                selector: 'node.is_not_virtual',
            },
            {
                content: copyIdTranslation,
                hasTrailingDivider: true,
                id: 'copy-id',
                onClickFunction: (evt: EventObject): void => {
                    const node = this.getClickedNode(evt);

                    this.copyTextToClipboard(node.dataNode.id);
                },
                selector: 'node.is_not_virtual',
            },
            {
                content: this.translocoService.translate('synoptic.contextMenu.centerOnMap'),
                hasTrailingDivider: false,
                id: 'center-edge-on-map',
                onClickFunction: async (evt: EventObject): Promise<void> => {
                    const node = this.getClickedNode(evt);

                    await this.redirectSynopticToMap(node, LayerStyleTypeEnum.LINE, synoptic);
                },
                selector: 'edge',
            },
            {
                content: this.translocoService.translate('synoptic.contextMenu.copyId'),
                hasTrailingDivider: true,
                id: 'copy-edge-id',
                onClickFunction: (evt: EventObject): void => {
                    const node = this.getClickedNode(evt);

                    this.copyTextToClipboard(node.dataNode.dataId);
                },
                selector: 'edge',
            },
            ...(this.networkPathService.canAccessNetworkPath()
                ? [this.generateNetworkPathMenuItem('node.is_not_virtual', synoptic)]
                : []),
        ];
    }

    private generateGraceThdMenu(synoptic: Signal<Synoptic | null>): MenuItem[] {
        return [
            {
                content: this.translocoService.translate('synoptic.contextMenu.getBay'),
                hasTrailingDivider: false,
                id: 'bay',
                onClickFunction: (evt: EventObject): void => {
                    const node = this.getClickedNode(evt);

                    this.synopticGraphService.focusedNode.set(node);
                },
                selector: 'node.is-bay',
            },
            {
                content: this.translocoService.translate('synoptic.contextMenu.getBox'),
                hasTrailingDivider: false,
                id: 'box',
                onClickFunction: (evt: EventObject): void => {
                    const node = this.getClickedNode(evt);

                    this.synopticGraphService.focusedNode.set(node);
                },
                selector: 'node.is-box',
            },
            {
                content: this.translocoService.translate('synoptic.contextMenu.exportFromItem'),
                hasTrailingDivider: false,
                id: 'export-svg',
                onClickFunction: (evt: EventObject): void => {
                    this.prepareNodeBeforeExportAsSvg(evt);
                },
                selector: 'node.is-bay, node.is-box',
            },
            {
                content: this.translocoService.translate('synoptic.contextMenu.centerOnMap'),
                hasTrailingDivider: false,
                id: 'center-box-on-map',
                onClickFunction: async (evt: EventObject): Promise<void> => {
                    const node = this.getClickedNode(evt);

                    await this.redirectSynopticToMap(node, LayerStyleTypeEnum.SYMBOL, synoptic);
                },
                selector: 'node',
            },
            this.generateCopyNodeMenu(PlanTypeEnum.BOX),
            this.generateCopyNodeMenu(PlanTypeEnum.BAY),
            {
                content: this.translocoService.translate('synoptic.contextMenu.centerOnMap'),
                hasTrailingDivider: false,
                id: 'center-edge-on-map',
                onClickFunction: async (evt: EventObject): Promise<void> => {
                    const node = this.getClickedNode(evt);

                    await this.redirectSynopticToMap(node, LayerStyleTypeEnum.LINE, synoptic);
                },
                selector: 'edge',
            },
            {
                content: this.translocoService.translate('copy'),
                hasTrailingDivider: false,
                id: 'copy-edge-menu',
                selector: 'edge',
                submenu: [
                    {
                        content: this.translocoService.translate('synoptic.contextMenu.copyGraceCode'),
                        hasTrailingDivider: false,
                        id: 'copy-edge-grace-code',
                        onClickFunction: (evt: EventObject): void => {
                            const node = this.getClickedNode(evt);

                            this.copyTextToClipboard(node.dataNode.dataId);
                        },
                        selector: 'edge',
                    },
                    {
                        content: this.translocoService.translate('synoptic.contextMenu.copyExternalCode'),
                        hasTrailingDivider: false,
                        id: 'copy-edge-codeext',
                        onClickFunction: (evt: EventObject): void => {
                            const node = this.getClickedNode(evt);

                            this.copyTextToClipboard(node.dataNode.externalCode);
                        },
                        selector: 'edge',
                    },
                    {
                        content: this.translocoService.translate('synoptic.contextMenu.copyLabel'),
                        hasTrailingDivider: false,
                        id: 'copy-edge-label',
                        onClickFunction: (evt: EventObject): void => {
                            const node = this.getClickedNode(evt);

                            this.copyTextToClipboard(node.dataNode.boxLabel);
                        },
                        selector: 'edge',
                    },
                ],
            },
            {
                content: this.translocoService.translate('exportOpticalRoute'),
                hasTrailingDivider: false,
                id: 'export-optical-routes',
                onClickFunction: async (evt: EventObject): Promise<void> => {
                    const node = this.getClickedNode(evt);

                    await this.opticalPathExportService.exportOpticalPath(
                        this.selectSynopticService.selectedDeposit()!.id,
                        node.dataNode.uuid,
                        this.opticalPathExportService.findBusinessIdentifier(node),
                    );
                },
                selector: 'node',
            },
            ...(this.networkPathService.canAccessNetworkPath()
                ? [this.generateNetworkPathMenuItem('node.is-bay, node.is-box', synoptic)]
                : []),
        ];
    }

    private generateNetworkPathMenuItem(selector: string, synoptic: Signal<Synoptic | null>): MenuItem {
        return {
            content: this.translocoService.translate('viewOpticalPath'),
            hasTrailingDivider: false,
            id: 'network-path',
            onClickFunction: (evt: EventObject): void => {
                const node = this.getClickedNode(evt);

                this.networkPathRedirection(node, synoptic);
            },
            selector,
        };
    }

    private getClickedNode(evt: EventObject): SelectedNode {
        return this.synopticPlanLayoutService.prepareNode(evt.target);
    }

    private async networkPathRedirection(node: SelectedNode, synoptic: Signal<Synoptic | null>): Promise<void> {
        const deposit: DepositWithStandard | null = this.selectSynopticService.selectedDeposit();
        const currentSynoptic: Synoptic | null = synoptic();

        if (!deposit || !currentSynoptic) {
            return;
        }

        const synopticItem: GraceThdSynopticItem | GenericSynopticItem | undefined = this.findSynopticItem(
            node,
            currentSynoptic,
        );

        if (!synopticItem) {
            return;
        }

        const layer: Layer | undefined = await this.findNetworkPathLayer(node, synopticItem, deposit.id);

        if (!layer) {
            return;
        }

        await this.networkPathService.networkPathRedirection(layer.id, node.dataNode.uuid);
    }

    private prepareNodeBeforeExportAsSvg(evt: EventObject): void {
        const node: NodeSingular = evt.target;

        const nodeInformations: SelectedNode = this.synopticPlanLayoutService.prepareNode(node);

        this.exportSynopticService.exportAsSvgFromNode(
            node,
            this.selectSynopticService.selectedDeposit()!,
            nodeInformations.dataNode.label,
        );
    }

    private async redirectSynopticToMap(
        node: SelectedNode,
        layerStyleType: LayerStyleTypeEnum,
        synoptic: Signal<Synoptic | null>,
    ): Promise<void> {
        const currentSynoptic: Synoptic | null = synoptic();
        const selectedDeposit: DepositWithStandard | null = this.selectSynopticService.selectedDeposit();

        if (!currentSynoptic || !selectedDeposit) {
            return;
        }

        MapBaseService.isFitBoundNeeded.set(false);

        const synopticItemFindMethod =
            layerStyleType === LayerStyleTypeEnum.LINE
                ? this.findLineSynopticItem(node)
                : this.findPointSynopticItem(node);

        const synopticItem: GraceThdSynopticItem | GenericSynopticItem | undefined =
            currentSynoptic.data.find(synopticItemFindMethod);

        if (this.homeService.isExternalPage()) {
            this.broadcastChannel.postMessage(<BroadcastChannelEvent<SynopticMapData>>{
                additionalData: {
                    depositId: selectedDeposit.id,
                    layerStyleType,
                    node: node.dataNode.uuid,
                    synopticItem,
                },
                message: BroadcastChannelMessageEnum.MOVE_MAP,
                source: BroadcastChannelSourceEnum.GRAPH,
            });

            return;
        }

        await this.mapSynopticRedirectionService.moveMapToEntityLocationFromSynopticNode(
            node.dataNode.uuid,
            selectedDeposit.id,
            layerStyleType,
            synopticItem,
        );
    }
}
